import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useGetActiveSubscriptionPlans } from "@/hooks/use-subscription";
import MailboxMainActions from "@/pages/mailbox/mailbox-main-actions";
import MailboxPageContent from "@/pages/mailbox/mailbox-page-content";
import SenderPageContent from "@/pages/mailbox/sender-page-content";
import SenderProfileMainActions from "@/pages/mailbox/sender-profile-main-actions";
import { useServices } from "@/services/hooks/useServices";
import {
  createFileRoute,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Info } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export const Route = createFileRoute("/_private/mailboxes/")({
  component: RouteComponent,
});

type TabType = "mailboxes" | "sender-profiles";

function RouteComponent() {
  const navigate = useNavigate();
  const searchParams = useSearch({
    from: "/_private/mailboxes/",
  });

  const selectedTab = useSearch({
    from: "/_private/mailboxes/",
    select: (search) =>
      ((search as unknown as { tab?: TabType })?.tab ?? "mailboxes") as TabType,
  });


  const { mailboxService } = useServices();

  const { data: activePlans } = useGetActiveSubscriptionPlans();

  // Calculate total available plans based on selected tab
  const totalAvailableTests = useMemo(() => {
    if (!activePlans) return 0;

    return activePlans.reduce((totalAvailable, subscription) => {
      // Calculate total quantity and used quantity across all plans in this subscription
      const { totalQuantity, totalUsed } = subscription.plans.reduce(
        (acc, plan) => {
          // For sender-profiles tab: only count seed list plans (isOnDemandSenderProfile)
          // For mailboxes tab: only count B2B and B2C plans
          if (selectedTab === "sender-profiles") {
            // Only count seed list plans
            const isSeedListPlan = plan?.planId?.isOnDemandSenderProfile || false;
            if (!isSeedListPlan) {
              return acc;
            }
          } else {
            // Only count B2B and B2C plans (exclude seed list plans)
            const isSeedListPlan = plan?.planId?.isOnDemandSenderProfile || false;
            if (isSeedListPlan) {
              return acc;
            }
            // Check if it's B2B or B2C category
            const category = plan?.planId?.category || "";
            if (category !== "B2B" && category !== "B2C") {
              return acc;
            }
          }

          return {
            totalQuantity: acc.totalQuantity + (plan.quantity || 0),
            totalUsed: acc.totalUsed + (plan.assignedMailboxes?.length || 0),
          };
        },
        { totalQuantity: 0, totalUsed: 0 }
      );

      // Add available plans (total quantity minus used) to the running total
      return totalAvailable + (totalQuantity - totalUsed);
    }, 0);
  }, [activePlans, selectedTab]);

  useEffect(() => {
    const code = (searchParams as unknown as { code?: string })?.code;
    const isGoogle =
      (searchParams as unknown as { state?: string })?.state === "google_auth";
    const isMicrosoft =
      (searchParams as unknown as { state?: string })?.state === "ms_auth";
    // console.log(code, isGoogle, isMicrosoft, searchParams);

    const handleCallback = async () => {
      if (code && isGoogle) {
        try {
          toast.info("Finalizing Google connection...");
          await mailboxService.googleOauthCallback(code);
          toast.success("Google mailbox connected successfully!");
        } catch (error) {
          console.error("Google OAuth Callback Error:", error);
          toast.error(
            "Failed to connect Google mailbox. Please try again or contact support."
          );
        } finally {
          navigate({ to: Route.to, search: {}, replace: true });
        }
      } else if (code && isMicrosoft) {
        try {
          toast.info("Finalizing Microsoft connection...");
          await mailboxService.microsoftOauthCallback(code);
          toast.success("Microsoft mailbox connected successfully!");
        } catch (error) {
          console.error("Microsoft OAuth Callback Error:", error);
          toast.error(
            "Failed to connect Microsoft mailbox. Please try again or contact support."
          );
        } finally {
          navigate({ to: Route.to, search: {}, replace: true });
        }
      }
    };

    if (code && (isGoogle || isMicrosoft)) {
      handleCallback();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, mailboxService, navigate]);

  return (
    <div className="w-full">
      <main className="w-full max-w-full">
        <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
          <div className="flex items-center gap-2 min-w-0">
            <h1 className="page-heading truncate">
              Email Management
            </h1>
          </div>
          <div className="flex items-center gap-2 text-xs sm:text-sm shrink-0">
            <div className="font-medium whitespace-nowrap">
              Available active plans:
            </div>
            <div className="font-semibold text-primary">
              {totalAvailableTests}
            </div>
          </div>
        </div>

        <Tabs
          value={selectedTab}
          className=""
          onValueChange={(value) => {
            navigate({ to: Route.to, search: { tab: value }, replace: true });
          }}
        >
          <div className="flex flex-col gap-2 md:flex-row md:justify-between md:items-center md:gap-4 mb-4">
            <div className="flex items-center gap-2">
              <TabsList className="w-full sm:w-max justify-start mb-2 sm:mb-0 border">
                <TabsTrigger value="mailboxes" className="flex-1 sm:flex-none">
                  Mailboxes
                </TabsTrigger>
                <TabsTrigger
                  value="sender-profiles"
                  className="flex-1 sm:flex-none flex items-center gap-2"
                >
                  Sender Profiles
                  {/* <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info 
                          className="h-3.5 w-3.5 hover:bg-gray-200 rounded p-0.5" 
                        />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="max-w-xs">
                          Sender profiles are distinct from mailboxes. This section is tailored for users of Salesforce, HubSpot, Mailmodo, or any email marketing platform that does not provide SMTP and IMAP credentials.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider> */}
                </TabsTrigger>
              </TabsList>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              {selectedTab === "mailboxes" ? (
                <MailboxMainActions />
              ) : (
                <SenderProfileMainActions />
              )}
            </div>
          </div>

          <TabsContent value="mailboxes" className="w-full max-w-full">
            <MailboxPageContent />
          </TabsContent>

          <TabsContent value="sender-profiles" className="w-full max-w-full">
            <SenderPageContent />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
